import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { X, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const NPS_LABELS = [
  "Not at all likely",
  "", "", "", "",
  "Neutral",
  "", "", "", "",
  "Extremely likely",
];

export default function AppNpsSurvey() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [score, setScore] = useState(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const checkShouldShow = useCallback(async () => {
    if (!user) return;
    // Never show NPS during onboarding
    if (!user.onboarding_complete) return;
    // iter315 — Admins are internal QA, not NPS audience. Suppressing
    // here keeps the survey from racing in on top of admin tooling
    // (5-second delay below was intercepting add-friend-btn clicks
    // during automated QA of /profile).
    if (user.is_admin) return;
    try {
      const res = await axios.get(`${API}/nps/should-show`);
      if (res.data?.should_show) {
        // Delay showing by 5 seconds after app stabilizes
        setTimeout(() => setShow(true), 5000);
      }
    } catch {
      // Silently fail — don't interrupt the user
    }
  }, [user]);

  useEffect(() => {
    checkShouldShow();
  }, [checkShouldShow]);

  const handleDismiss = async () => {
    setShow(false);
    try {
      await axios.post(`${API}/nps`, { dismissed: true });
    } catch {}
  };

  const handleSubmit = async () => {
    if (score === null) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/nps`, {
        score,
        comment: comment.trim(),
      });
      if (res.data?.submitted) {
        toast.success("Thanks for your feedback!");
      }
      setShow(false);
    } catch {
      toast.error("Failed to submit — try again later");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const getScoreColor = (n) => {
    if (score === null) {
      if (n <= 6) return "bg-red-50 text-red-600 border-red-200";
      if (n <= 8) return "bg-amber-50 text-amber-600 border-amber-200";
      return "bg-green-50 text-green-600 border-green-200";
    }
    if (n === score) {
      if (n <= 6) return "bg-red-500 text-white border-red-500 ring-2 ring-red-300";
      if (n <= 8) return "bg-amber-500 text-white border-amber-500 ring-2 ring-amber-300";
      return "bg-green-500 text-white border-green-500 ring-2 ring-green-300";
    }
    return "bg-slate-50 text-slate-400 border-slate-200";
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[250] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="nps-survey-modal"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold text-violet-500 uppercase tracking-wider">Quick Survey</p>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition"
              data-testid="nps-dismiss"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>

          <div className="px-5 pb-6">
            <h3 className="text-base font-bold text-slate-800 mb-1" data-testid="nps-title">
              How likely are you to recommend this app?
            </h3>
            <p className="text-xs text-slate-400 mb-4">On a scale of 0-10</p>

            {/* NPS Scale */}
            <div className="grid grid-cols-11 gap-1 mb-2" data-testid="nps-scale">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setScore(i)}
                  className={`aspect-square rounded-lg border text-xs font-bold flex items-center justify-center transition-all active:scale-90 ${getScoreColor(i)}`}
                  data-testid={`nps-score-${i}`}
                >
                  {i}
                </button>
              ))}
            </div>

            {/* Labels */}
            <div className="flex justify-between mb-4">
              <span className="text-[9px] text-slate-400">Not likely</span>
              <span className="text-[9px] text-slate-400">Extremely likely</span>
            </div>

            {/* Score feedback */}
            {score !== null && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-3">
                <div className={`px-3 py-2 rounded-lg text-xs font-medium text-center ${
                  score <= 6 ? "bg-red-50 text-red-700" : score <= 8 ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"
                }`}>
                  {score <= 6 ? "We're sorry to hear that. How can we improve?" :
                   score <= 8 ? "Thanks! What would make it a 10?" :
                   "Awesome! We're glad you love it!"}
                </div>

                {/* Comment toggle / field */}
                {!showComment ? (
                  <button
                    onClick={() => setShowComment(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-500 font-medium hover:bg-slate-50 transition"
                    data-testid="nps-add-comment"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Add a comment (optional)
                  </button>
                ) : (
                  <textarea
                    autoFocus
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full h-20 px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400"
                    placeholder={score <= 6 ? "What could we do better?" : "Any thoughts to share?"}
                    data-testid="nps-comment"
                  />
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-3 rounded-xl bg-violet-500 text-white text-sm font-semibold shadow-md shadow-violet-500/20 hover:bg-violet-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  data-testid="nps-submit"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
